import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Key, DownloadSimple, Copy, Trash, Desktop, TerminalWindow } from "@phosphor-icons/react";
import { UploadKeys } from "../lib/api";
import { WatcherSteps } from "../components/WatcherSteps";
import { WatcherStatus } from "../components/WatcherStatus";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

export default function WatcherPage() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("Office PC");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => UploadKeys.list().then(setKeys).catch(() => toast.error("Could not load keys"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const k = await UploadKeys.create(label.trim() || "Desktop Watcher");
      setFresh(k);
      await load();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Could not create key"));
    } finally { setBusy(false); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.label}"? Watchers using it stop uploading immediately.`)) return;
    await UploadKeys.revoke(k.id);
    if (fresh?.id === k.id) setFresh(null);
    toast.success("Key revoked");
    load();
  };

  const copy = (text) => {
    if (!navigator.clipboard) return toast.error("Copy failed — select the text manually");
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"), () => toast.error("Copy failed — select the text manually"));
  };
  const active = keys.filter((k) => !k.revoked_at);

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto page-fade" data-testid="watcher-page">
      <p className="label-mono text-neutral-500">DESKTOP / AUTO-INGEST</p>
      <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter flex items-center gap-3">
        <Desktop weight="bold" /> DESKTOP WATCHER
      </h1>
      <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
        A Windows script that watches a folder or SD card and uploads new drone and studio photos into your account.
        Photos with GPS go to <span className="font-mono">AERIAL-&lt;date&gt;</span>, photos without GPS go to <span className="font-mono">TABLETOP-&lt;date&gt;</span>.
      </p>

      <WatcherStatus />

      <section className="border-2 border-black mt-6" data-testid="watcher-keys-section">
        <div className="px-4 py-3 border-b-2 border-black flex items-center gap-2 font-display font-bold tracking-tighter">
          <Key weight="bold" size={18} /> 01 · UPLOAD KEYS
        </div>
        <div className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
              className="border-2 border-black px-3 py-2 font-mono text-sm flex-1" placeholder="Key label (e.g. Office PC)"
              data-testid="watcher-key-label-input" />
            <button onClick={create} disabled={busy} className="btn-flat" data-testid="watcher-create-key-btn">
              <Key weight="bold" size={18} /> {busy ? "Creating…" : "Create upload key"}
            </button>
          </div>
          {fresh && (
            <div className="border-2 border-[#FF2A2A] bg-[#FFF5F5] p-3" data-testid="watcher-new-key">
              <p className="label-mono text-[#FF2A2A]">COPY NOW — THIS KEY IS SHOWN ONLY ONCE</p>
              <div className="flex gap-2 mt-2 items-center">
                <code className="font-mono text-xs break-all flex-1 bg-white border-2 border-black px-2 py-2" data-testid="watcher-new-key-value">{fresh.key}</code>
                <button onClick={() => copy(fresh.key)} className="btn-flat-outline" data-testid="watcher-copy-key-btn"><Copy weight="bold" size={16} /></button>
              </div>
            </div>
          )}
          <KeyList keys={keys} onRevoke={revoke} />
          <p className="text-xs text-neutral-500 font-mono">{active.length} ACTIVE · A KEY CAN ONLY UPLOAD INTO YOUR OWN SESSIONS</p>
        </div>
      </section>

      <section className="border-2 border-black mt-6" data-testid="watcher-download-section">
        <div className="px-4 py-3 border-b-2 border-black flex items-center gap-2 font-display font-bold tracking-tighter">
          <TerminalWindow weight="bold" size={18} /> 02 · DOWNLOAD + RUN
        </div>
        <div className="p-4">
          <a href={UploadKeys.scriptUrl} className="btn-flat inline-flex" data-testid="watcher-download-script-btn">
            <DownloadSimple weight="bold" size={18} /> Download aiis-watcher.ps1
          </a>
          <WatcherSteps uploadKey={fresh?.key} onCopy={copy} />
        </div>
      </section>
    </div>
  );
}

function KeyList({ keys, onRevoke }) {
  if (!keys.length) return <p className="text-sm text-neutral-500" data-testid="watcher-no-keys">No keys yet.</p>;
  return (
    <ul className="divide-y-2 divide-black border-2 border-black" data-testid="watcher-key-list">
      {keys.map((k) => (
        <li key={k.id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`watcher-key-${k.id}`}>
          <div className="min-w-0">
            <p className="font-mono text-sm font-bold truncate">{k.label} <span className="text-neutral-500">{k.prefix}…</span></p>
            <p className="font-mono text-[11px] text-neutral-500">CREATED {fmt(k.created_at)} · LAST USED {fmt(k.last_used_at)} · {k.use_count || 0} CALLS</p>
          </div>
          {k.revoked_at ? (
            <span className="chip" data-testid={`watcher-key-revoked-${k.id}`}>REVOKED</span>
          ) : (
            <button onClick={() => onRevoke(k)} className="btn-flat-outline" data-testid={`watcher-revoke-${k.id}`}>
              <Trash weight="bold" size={16} /> Revoke
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
